/* eslint-disable react/require-default-props, react/forbid-prop-types, jsx-a11y/label-has-associated-control, no-nested-ternary, max-len */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import ApiService from '../../../../services/api.service';
import NotificationsStatusEnum from '../../enums/notifications-status.enum';
import NotificationsTypeEnum from '../../enums/notifications-type.enum';

function ReportIssueModal({ isOpen, onClose, skill, detail }) {
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [disabledMaterialIds, setDisabledMaterialIds] = useState([]);

  // Build materials array from detail - handle both formats
  const materials = React.useMemo(() => {
    // If detail has materials array (from v3), use it
    if (detail.materials?.length > 0) {
      return detail.materials;
    }
    // If detail has materialName/link directly (from v2), create a single-item array
    if (detail.materialName || detail.link) {
      return [
        {
          name: detail.materialName || detail.name,
          link: detail.link || '',
          type: detail.materialType || '',
          itemId: detail.itemId,
        },
      ];
    }
    return [];
  }, [detail]);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const notifications = await ApiService.developer.getNotifications();
        const disabledIds = notifications.flatMap(
          (notification) => notification.details?.materialIds || [],
        );
        setDisabledMaterialIds(disabledIds);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };

    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  // Auto-select material if only one is available
  useEffect(() => {
    if (materials.length === 1) {
      const mat = materials[0];
      const matId = mat.itemId || detail.itemId;
      if (!disabledMaterialIds.includes(matId)) {
        setSelectedMaterials([mat.name]);
      }
    }
  }, [materials, disabledMaterialIds, detail.itemId]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setDescription('');
      // Pre-select if only one material
      if (materials.length === 1) {
        const mat = materials[0];
        const matId = mat.itemId || detail.itemId;
        if (!disabledMaterialIds.includes(matId)) {
          setSelectedMaterials([mat.name]);
        } else {
          setSelectedMaterials([]);
        }
      } else {
        setSelectedMaterials([]);
      }
    }
  }, [isOpen, materials, disabledMaterialIds, detail.itemId]);

  const handleToggleMaterial = (materialName) => {
    const material = skill.details?.find((d) => d.materialName === materialName);
    if (material && disabledMaterialIds.includes(material.itemId)) {
      return;
    }

    setSelectedMaterials((prev) =>
      prev.includes(materialName)
        ? prev.filter((m) => m !== materialName)
        : [...prev, materialName],
    );
  };

  const handleSave = async () => {
    if (selectedMaterials.length === 0 || !description.trim()) return;

    setIsLoading(true);
    try {
      const user = await ApiService.developer.getMySelf();
      const { _id: userId, departmentId } = user;

      const heads = await ApiService.developer.getDepartments();
      const head = heads.find((el) => el._id === departmentId);
      const headIds = head?.headIds || [];

      const selectedMaterialData = selectedMaterials.map((name) => {
        const mat = materials.find((m) => m.name === name);
        const detailMat = skill.details?.find((d) => d.materialName === name);
        return {
          id: detailMat?.itemId || mat?.itemId,
          type: mat?.type || detailMat?.materialType || '',
          link: mat?.link || detailMat?.link || '',
        };
      });

      const data = {
        userId,
        type: NotificationsTypeEnum.REPORT,
        status: NotificationsStatusEnum.PENDING,
        details: {
          materialIds: selectedMaterialData.map((m) => m.id),
          materialTypes: selectedMaterialData.map((m) => m.type),
          links: selectedMaterialData.map((m) => m.link),
          parentId: skill._id || skill.itemId,
          parentName: skill.name,
          detailName: detail.name,
          materialNames: selectedMaterials,
          description,
        },
        headIds,
      };

      await ApiService.developer.sendReport(data);
      onClose();
    } catch (error) {
      console.error('Error sending report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const isSaveDisabled = selectedMaterials.length === 0 || !description.trim() || isLoading;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        aria-label="Close modal"
      />

      <div className="relative z-50 w-full max-w-md rounded-lg border bg-background p-6 shadow-lg">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex flex-col items-center gap-2 mb-6">
          <div className="rounded-full bg-destructive/10 p-3">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <h2 className="text-lg font-semibold text-center">Report Issue</h2>
          <p className="text-sm text-muted-foreground text-center">{detail.name}</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Lesson materials</label>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {materials.length > 0 ? (
                materials.map((material) => {
                  const detailMat = skill.details?.find((d) => d.materialName === material.name);
                  const matId = material.itemId || detailMat?.itemId;
                  const isDisabled = disabledMaterialIds.includes(matId);
                  const isSelected = selectedMaterials.includes(material.name);
                  const isSingleMaterial = materials.length === 1;

                  return (
                    <label
                      key={material.name}
                      className={`flex items-center gap-3 p-2 rounded-md border cursor-pointer transition-colors ${
                        isDisabled
                          ? 'opacity-50 cursor-not-allowed bg-muted'
                          : isSelected
                          ? 'border-primary bg-primary/5'
                          : 'hover:bg-muted/50'
                      }`}
                    >
                      {!isSingleMaterial && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleMaterial(material.name)}
                          disabled={isDisabled}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                      )}
                      <span className={`text-sm ${isDisabled ? 'text-muted-foreground' : ''}`}>
                        {material.name}
                        {isDisabled && ' (Already reported)'}
                      </span>
                    </label>
                  );
                })
              ) : (
                <p className="text-sm text-muted-foreground p-2 rounded-md border bg-muted/30">
                  This lesson has no materials attached
                </p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="description" className="text-sm font-medium mb-2 block">
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the issue..."
              className="w-full min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaveDisabled} className="flex-1">
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Report'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

ReportIssueModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  skill: PropTypes.shape({
    _id: PropTypes.string,
    itemId: PropTypes.string,
    name: PropTypes.string,
    details: PropTypes.array,
  }).isRequired,
  detail: PropTypes.shape({
    itemId: PropTypes.string,
    name: PropTypes.string,
    materialName: PropTypes.string,
    materialType: PropTypes.string,
    link: PropTypes.string,
    materials: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        link: PropTypes.string,
        type: PropTypes.string,
      }),
    ),
  }).isRequired,
};

export default ReportIssueModal;
